import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, take } from 'rxjs';
import moment from 'moment-timezone';
import { IntelligenceModels } from '@models/intelligence.model';
import { WallV2Model } from '@models/wall-v2.model';
import { routerSegments } from '@consts/routes';
import { selectUserTimezone } from '@states/user/user.selectors';
import { SharedActions } from '@states/shared/shared.action-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { ConfirmDialogModel, ConfirmDialogService, PreloaderColor, UiPreloaderComponent, UiFilterChipComponent, UiCameraSelectButtonComponent } from '@ui';
import { CameraPickerModalComponent } from '@modals/camera-picker-modal/camera-picker-modal.component';
import { CameraPickerModels } from '@modals/camera-picker-inline/models/models';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { UtilsService } from '../../../../../../edge/utils.service';
import { WallV2Service } from '../../../../../../services/wall-v2.service';
import { AppState } from '../../../../../../store/app.state';
import { generateCameraIdsIncludesExcludes } from '../../../../../../helpers/common.helpers';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { TagFilterComponent } from '../../../../../../pages/database/pages/tags/components/tag-filter/tag-filter.component';
import { SaveQuickLiveViewAsWall } from '../../../../../../pages/wall-v2/modals/save-quick-live-view-as-wall/save-quick-live-view-as-wall';

/** Matches the archive panel's `mdyTimeSec` format (e.g. 06/08/26 11:26:53 AM). */
const DATE_FORMAT = 'MM/DD/YY h:mm:ss A';

@Component({
  selector: 'app-walls-result',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatTooltip,
    MatMenuModule,
    MatSelectModule,
    UiPreloaderComponent,
    UiFilterChipComponent,
    UiCameraSelectButtonComponent,
    UiSearchComponent,
    TagFilterComponent,
  ],
  templateUrl: './walls-result.component.html',
  styleUrls: ['./walls-result.component.scss'],
})
export class WallsResultComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly utilsService = inject(UtilsService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly wallV2Service = inject(WallV2Service);

  @ViewChild('sentinel') private sentinel?: ElementRef<HTMLElement>;
  private observer: IntersectionObserver | null = null;

  protected readonly PreloaderColor = PreloaderColor;

  protected readonly isLoading = signal<boolean>(false);
  protected readonly deletingWallId = signal<string | null>(null);
  protected readonly editingWallId = signal<string | null>(null);
  protected readonly isCreating = signal<boolean>(false);

  protected readonly userTimezone = this.store$.selectSignal(selectUserTimezone);

  protected readonly response = computed<IntelligenceModels.Walls.Response | null>(() => {
    const result = this.resultsService.current();
    if (result?.type !== 'wall') return null;
    return result.data as IntelligenceModels.Walls.Response;
  });

  protected readonly walls = computed(() => this.response()?.walls ?? []);
  protected readonly hasMore = computed(() => this.response()?.hasMore ?? false);

  // ── Filters ────────────────────────────────────────────────────────────────
  // Local-only state: never synced to the URL and never dispatched to NgRx. The
  // camera/tag catalogues below are read-only store reads used solely for option
  // lists and chip labels. A filter change re-queries the server and replaces the
  // current panel result in place (no new history entry).

  /** Read-only reference catalogues (option lists + chip labels only). */
  protected readonly camerasEntities = this.store$.selectSignal(CameraSelectors.selectAllCamerasEntities);
  private readonly allCameras = this.store$.selectSignal(CameraSelectors.selectAllCameras);
  protected readonly tagEntities = this.store$.selectSignal(DatabaseTagsSelectors.selectDatabaseTagEntities);

  protected readonly searchQuery = signal<string>('');
  protected readonly selectedTypes = signal<boolean[]>([]);
  protected readonly selectedTags = signal<string[]>([]);
  protected readonly selectedCameras = signal<Dictionary<CameraPickerModels.SelectedCamera>>({});
  public readonly tagsFilterControl = new FormControl<string[]>([], { nonNullable: true });

  /** True while a filter re-query is in flight (panel-level loader). */
  protected readonly isFiltering = signal<boolean>(false);

  protected readonly selectedCameraList = computed<CameraPickerModels.SelectedCamera[]>(() =>
    Object.values(this.selectedCameras()),
  );

  protected readonly hasActiveFilters = computed<boolean>(
    () =>
      !!this.searchQuery() ||
      this.selectedTypes().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedCameraList().length > 0,
  );

  /**
   * Reset the filters when the user navigates to a different history entry (a new
   * wall query), so a previous entry's filters/chips don't leak onto another set.
   * In-place filter re-queries keep the same index, so they never trip this.
   */
  private lastEntryPosition: number = this.resultsService.currentPosition();
  private readonly resetOnNavigation = effect(() => {
    const position = this.resultsService.currentPosition();
    if (position === this.lastEntryPosition) return;
    this.lastEntryPosition = position;
    this.resetFilters();
  });

  public ngOnInit(): void {
    // Populate the tag catalogue for the tag filter's options/labels if it is empty.
    if (!Object.keys(this.tagEntities()).length) {
      this.store$.dispatch(DatabaseTagsActions.getDatabaseTags());
    }

    // app-tag-filter writes through this control; mirror it into a signal and
    // re-query (debounced so rapid multi-select doesn't fire a request per click).
    this.tagsFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((tags) => {
        this.selectedTags.set(tags ?? []);
        void this.reload();
      });
  }

  public ngAfterViewInit(): void {
    if (!this.sentinel) return;

    const root = this.findScrollParent(this.host.nativeElement);
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void this.loadMore();
        }
      },
      { root, rootMargin: '200px', threshold: 0 },
    );
    this.observer.observe(this.sentinel.nativeElement);
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  protected trackByWallId(_index: number, item: IntelligenceModels.Walls.Item): string {
    return item._id;
  }

  /**
   * Drill into the wall's camera previews. Shown as an overlay (not a history
   * entry); the context bar's back crumb returns to this wall list.
   */
  protected openWall(item: IntelligenceModels.Walls.Item): void {
    this.resultsService.setOverlay({ _id: item._id, name: item.name }, 'wall-camera', item.name);
  }

  protected editWall(item: IntelligenceModels.Walls.Item): void {
    if (this.editingWallId() || this.deletingWallId()) return;

    this.editingWallId.set(item._id);
    this.wallV2Service
      .getById(item._id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (wall) => {
          this.editingWallId.set(null);
          this.resultsService.setOverlay(
            {
              name: wall.name,
              wall,
            },
            'create-wall',
            wall.name,
          );
        },
        error: (err) => {
          this.editingWallId.set(null);
          this.store$.dispatch(SharedActions.showMessage({ error: this.utilsService.errMessage(err) }));
        },
      });
  }

  /** Translation key for the wall type chip (Private vs Public) — matches the type filter. */
  protected typeLabelKey(item: IntelligenceModels.Walls.Item): string {
    return item.isPrivate ? 'COMMON.PRIVATE' : 'COMMON.PUBLIC';
  }

  protected formatDate(value: number | null): string {
    if (!value) return '—';
    return moment(value).tz(this.userTimezone()).format(DATE_FORMAT);
  }

  /** Open the walls list page (`/wall-v2/list`) in a new tab so the Cmd+K session stays open. */
  protected openWallsPage(): void {
    this.openInNewTab([routerSegments.wallV2, routerSegments.list]);
  }

  protected createWall(): void {
    if (this.isCreating()) return;

    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '80vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((picked?: Dictionary<CameraPickerModels.SelectedCamera>) => {
        const cameras = Object.values(picked ?? {}).filter(
          (camera): camera is CameraPickerModels.SelectedCamera => !!camera,
        );
        if (!cameras.length) return;
        this.confirmCreateWall(cameras);
      });
  }

  // ── Filter handlers ─────────────────────────────────────────────────────────

  /** ui-search debounces internally; ignore its initial echo of the current value. */
  protected onSearchQuery(value: string): void {
    const next = value ?? '';
    if (next === this.searchQuery()) return;
    this.searchQuery.set(next);
    void this.reload();
  }

  protected onTypeChange(event: MatSelectChange): void {
    this.selectedTypes.set(event.value ?? []);
    void this.reload();
  }

  /** Open the shared multi-camera picker, seeded with the current selection. */
  protected openSelectCameraModal(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
      selectedValue: this.selectedCameras(),
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '80vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((cameras?: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (!cameras) return;
        this.selectedCameras.set(cameras);
        void this.reload();
      });
  }

  protected onRemoveType(value: boolean): void {
    this.selectedTypes.update((types) => types.filter((type) => type !== value));
    void this.reload();
  }

  protected onRemoveSearch(): void {
    this.searchQuery.set('');
    void this.reload();
  }

  /** Removing a tag chip writes through the control (valueChanges → reload). */
  protected onRemoveTag(tagId: string): void {
    const next = (this.tagsFilterControl.value ?? []).filter((id) => id !== tagId);
    this.tagsFilterControl.setValue(next);
  }

  protected onRemoveCamera(cameraId: string): void {
    this.selectedCameras.update((map) => {
      const next = { ...map };
      delete next[cameraId];
      return next;
    });
    void this.reload();
  }

  private resetFilters(): void {
    this.searchQuery.set('');
    this.selectedTypes.set([]);
    this.selectedTags.set([]);
    // Skip emitEvent so the reset does not trigger a re-query.
    this.tagsFilterControl.setValue([], { emitEvent: false });
    this.selectedCameras.set({});
  }

  private confirmCreateWall(cameras: CameraPickerModels.SelectedCamera[]): void {
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'CONFIRM_MODALS.LIVE_VIEW_SAVE_AS_WALL.TITLE',
        msg: 'CONFIRM_MODALS.LIVE_VIEW_SAVE_AS_WALL.MSG',
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.askWallName(cameras);
      });
  }

  private askWallName(cameras: CameraPickerModels.SelectedCamera[]): void {
    this.dialog
      .open(SaveQuickLiveViewAsWall, {
        panelClass: 'modal-no-padding',
        width: '300px',
        disableClose: true,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((name?: string) => {
        if (!name) return;
        this.submitCreateWall(cameras, name);
      });
  }

  private submitCreateWall(cameras: CameraPickerModels.SelectedCamera[], name: string): void {
    this.isCreating.set(true);
    this.toolsService
      .createWall(this.buildWallCreateDto(cameras, name))
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isCreating.set(false);
          this.store$.dispatch(
            SharedActions.showMessage({
              success: this.translate.instant('SEARCH_GLOBAL_MODAL.WALL_RESULT.CREATE_SUCCESS'),
            }),
          );
          void this.reload();
        },
        error: (err) => {
          this.isCreating.set(false);
          this.store$.dispatch(SharedActions.showMessage({ error: this.utilsService.errMessage(err) }));
        },
      });
  }

  private buildWallCreateDto(
    cameras: CameraPickerModels.SelectedCamera[],
    name: string,
  ): WallV2Model.WallCreateDto {
    const layoutMap = WallV2Model.cameraQuantityLayout as Record<number, WallV2Model.WallLayout>;
    const layoutKey = Object.keys(WallV2Model.cameraQuantityLayout)
      .map(Number)
      .find((quantity) => quantity >= cameras.length);
    const layout = layoutKey ? layoutMap[layoutKey] : WallV2Model.WallLayout.GRID_30;
    const selectedCameras: WallV2Model.SelectedCamera[] = cameras.map(({ cameraId, edgeId, locationId }) => ({
      cameraId,
      edgeId,
      locationId,
    }));
    const tiles = new Array(WallV2Model.wallLayoutCameraCountV2[layout]).fill(null).map((_tile, index) => ({
      ...WallV2Model.defaultSetTile,
      camera: selectedCameras[index] ?? WallV2Model.defaultSetTile.camera,
    }));

    return {
      ...WallV2Model.defaultWallCreate,
      name,
      sets: [
        {
          name: 'Set 1',
          duration: 0,
          layout,
          tiles,
        },
      ],
    };
  }

  /**
   * Re-query the walls endpoint with the current filters and replace the panel's
   * data in place. Pagination restarts (fresh response, `latestTs` reset).
   */
  private async reload(): Promise<void> {
    const camera = this.buildCameraFilter();
    const params: IntelligenceModels.Walls.ListParams = {
      limit: this.response()?.filter?.limit ?? IntelligenceModels.Walls.DEFAULT_LIMIT,
      query: this.searchQuery() || undefined,
      isPrivate: this.selectedTypes().length ? this.selectedTypes() : undefined,
      tags: this.selectedTags().length ? this.selectedTags() : undefined,
      cameraIdsIncludes: camera.cameraIdsIncludes.length ? camera.cameraIdsIncludes : undefined,
      cameraIdsExcludes: camera.cameraIdsExcludes.length ? camera.cameraIdsExcludes : undefined,
    };

    this.isFiltering.set(true);
    try {
      const page = await this.toolsService.fetchWalls(params);
      this.resultsService.replaceWallsInCurrent(page);
    } catch (err) {
      this.store$.dispatch(SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }));
    } finally {
      this.isFiltering.set(false);
    }
  }

  /** Convert the picked cameras into include/exclude ids, scoped to the org's cameras. */
  private buildCameraFilter(): { cameraIdsIncludes: string[]; cameraIdsExcludes: string[] } {
    const picked = this.selectedCameras();
    if (!Object.keys(picked).length) return { cameraIdsIncludes: [], cameraIdsExcludes: [] };
    const allowedCameraIds = this.allCameras()
      .map((camera) => camera.edgeOnly?.cameraId)
      .filter((id): id is string => !!id);
    return generateCameraIdsIncludesExcludes(picked, allowedCameraIds);
  }

  /** Confirm, then delete the wall; swaps the row's menu icon for an inline loader while in flight. */
  protected deleteWall(item: IntelligenceModels.Walls.Item): void {
    if (this.deletingWallId() || this.editingWallId()) return;
    this.confirmDialog
      .open({
        // ui-confirm-dialog translates these via the `translate` pipe, so pass raw keys.
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'SEARCH_GLOBAL_MODAL.WALL_RESULT.DELETE_TITLE',
        msg: 'SEARCH_GLOBAL_MODAL.WALL_RESULT.DELETE_MESSAGE',
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.deletingWallId.set(item._id);
        this.toolsService
          .deleteWall(item._id)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.resultsService.removeWallFromCurrent(item._id);
              this.deletingWallId.set(null);
            },
            error: (err) => {
              this.deletingWallId.set(null);
              this.store$.dispatch(
                SharedActions.showMessage({ error: this.utilsService.errMessage(err) }),
              );
            },
          });
      });
  }

  /** Open the target app page in a new tab so the current Cmd+K session stays open. */
  private openInNewTab(segments: string[]): void {
    const tree = this.router.createUrlTree(['/', ...segments]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin ? `${window.location.origin}${path}` : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Fetch the next page using the oldest displayed wall's createdAt as the
   * cursor (createdAt <= cursor; the boundary duplicate is de-duped on append).
   */
  private async loadMore(): Promise<void> {
    if (this.isFiltering() || this.isLoading() || !this.hasMore()) return;

    const filter = this.response()?.filter;
    const walls = this.walls();
    if (!filter || !walls.length) return;

    const cursor = walls[walls.length - 1].createdAt;
    if (!cursor) return;

    this.isLoading.set(true);
    try {
      const camera = this.buildCameraFilter();
      // Keep the current filters (if any) applied while paging; otherwise page
      // the original tool result using its own query.
      const params: IntelligenceModels.Walls.ListParams = this.hasActiveFilters()
        ? {
            limit: filter.limit,
            latestTs: cursor,
            query: this.searchQuery() || undefined,
            isPrivate: this.selectedTypes().length ? this.selectedTypes() : undefined,
            tags: this.selectedTags().length ? this.selectedTags() : undefined,
            cameraIdsIncludes: camera.cameraIdsIncludes.length ? camera.cameraIdsIncludes : undefined,
            cameraIdsExcludes: camera.cameraIdsExcludes.length ? camera.cameraIdsExcludes : undefined,
          }
        : { limit: filter.limit, query: filter.query ?? undefined, latestTs: cursor };
      const page = await this.toolsService.fetchWalls(params);
      this.resultsService.appendWallsToCurrent(page.walls, cursor, !!page.hasMore);
    } finally {
      this.isLoading.set(false);
    }
  }

  private findScrollParent(element: HTMLElement | null): HTMLElement | null {
    let node = element?.parentElement ?? null;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }
}
